import { Divider, Tag } from "antd";
import {
  Briefcase,
  Building,
  Calendar,
  CircleCheckBig,
  Clock,
  CreditCard,
  DollarSign,
  FileText,
  GraduationCap,
  Hash,
  Heart,
  Hexagon,
  Landmark,
  Mail,
  MapPin,
  Paperclip,
  Phone,
  Shield,
  ShieldCheck,
  User,
  UserCircle,
  Users,
} from "lucide-react";

import type { EmployeeCertificateVO, EmployeeDetailVO } from "@/api/employees/type";
import type { OrganizationOrgType } from "@/api/system/organizations/type";
import { DetailRow, SectionTitle } from "@/components/DetailBlocks";
import { OssPreviewImage } from "@/components/OssPreviewImage";
import type { RolePermOpenParams } from "@/components/RoleDetailModal";
import { ORGANIZATION_ORG_TYPE_META } from "@/config/system";
import { useDict } from "@/hooks/useDict";
import { cn } from "@/lib/cn";

/**
 * 从 templateType 解析组织类型元数据
 * @param templateType 模板/组织类型编码
 */
const resolveOrgTypeMeta = (templateType?: string) => {
  if (!templateType || !(templateType in ORGANIZATION_ORG_TYPE_META)) return undefined;
  return ORGANIZATION_ORG_TYPE_META[templateType as OrganizationOrgType];
};

/**
 * 取当前员工主工作身份（用于单位类型 Tag）
 * @param detail 员工详情
 */
const getPrimaryWorkIdentity = (detail: EmployeeDetailVO) =>
  detail.workIdentities?.find((item) => item.employeeId === detail.employeeId) ??
  detail.workIdentities?.[0];

/**
 * 格式化月薪展示
 * @param amount 金额
 */
const formatMonthlySalary = (amount?: number) =>
  amount != null && !Number.isNaN(amount) ? `${amount}元/月` : "—";

/** 员工详情头部名片 props */
export interface EmployeeHeaderCardProps {
  detail: EmployeeDetailVO;
  statusLabel: string;
  statusColor: string;
}

/** 员工详情头部名片 */
export const EmployeeHeaderCard = ({
  detail,
  statusLabel,
  statusColor,
}: EmployeeHeaderCardProps) => {
  /** 个人资料 */
  const profile = detail.profile;
  /** 工作信息 */
  const work = detail.workInfo;
  /** 显示名称 */
  const displayName = profile?.name ?? "—";
  /** 角色名称 */
  const roleName = work?.staffRoleLabel ?? work?.staffRole ?? "—";
  /** 店铺名称 */
  const shopName = work?.shopName ?? detail.shopName ?? "—";
  /** 主工作身份 */
  const identity = getPrimaryWorkIdentity(detail);
  /** 组织类型元数据 */
  const unitMeta = resolveOrgTypeMeta(identity?.templateType);
  /** 头像 OSS 地址，无值时回退姓名首字 */
  const avatarUrl = profile?.avatarUrl;
  /** 无头像时的姓名首字占位 */
  const avatarFallback = displayName.charAt(0) || "—";

  return (
    <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-xl border">
      <div className="h-14 w-14 shrink-0 overflow-hidden rounded-xl bg-orange-500 text-xl font-bold text-white">
        <OssPreviewImage
          fileUrl={avatarUrl}
          alt={displayName}
          fallback={
            <span className="flex h-full w-full items-center justify-center">{avatarFallback}</span>
          }
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <h3 className="font-bold text-lg m-0">{displayName}</h3>
          <Tag bordered color={statusColor} className="m-0 text-[10px]">
            {statusLabel}
          </Tag>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-500 flex-wrap">
          <span className="font-mono">{work?.employeeNo ?? "—"}</span>
          <span>·</span>
          <span>{roleName}</span>
          {unitMeta ? (
            <>
              <span>·</span>
              <Tag
                bordered
                className={cn(
                  "m-0 text-[10px]",
                  unitMeta.bgColor,
                  unitMeta.color,
                  unitMeta.borderColor,
                )}
              >
                {unitMeta.shortLabel}
              </Tag>
            </>
          ) : null}
          <span>{shopName}</span>
        </div>
      </div>
    </div>
  );
};

/** 基础信息 Tab props */
export interface BasicInfoTabProps {
  detail: EmployeeDetailVO;
}

/**
 * 员工详情 - 基础信息 Tab（个人资料 + 紧急联系人）
 */
export const BasicInfoTab = ({ detail }: BasicInfoTabProps) => {
  const { getDictLabel } = useDict();
  const profile = detail.profile;

  return (
    <div className="space-y-4 max-h-[50vh] overflow-y-auto">
      <div>
        <SectionTitle icon={UserCircle} title="个人资料" />
        <div className="grid grid-cols-2 gap-x-6 gap-y-1">
          <DetailRow icon={UserCircle} label="昵称" value={profile?.name} labelClassName="w-24" />
          <DetailRow
            icon={User}
            label="性别"
            value={getDictLabel("gender", profile?.gender)}
            labelClassName="w-24"
          />
          <DetailRow icon={Calendar} label="生日" value={profile?.birthday} labelClassName="w-24" />
          <DetailRow
            icon={GraduationCap}
            label="学历"
            value={getDictLabel("education", profile?.education)}
            labelClassName="w-24"
          />
          <DetailRow
            icon={Heart}
            label="婚姻状况"
            value={getDictLabel("marital", profile?.maritalStatus)}
            labelClassName="w-24"
          />
          <DetailRow icon={Mail} label="邮箱" value={profile?.email} labelClassName="w-24" />
          <div className="col-span-2">
            <DetailRow
              icon={MapPin}
              label="通信地址"
              value={profile?.address}
              labelClassName="w-24"
            />
          </div>
        </div>
      </div>
      <Divider className="my-2" />
      <div>
        <SectionTitle icon={Heart} title="紧急联系人" />
        <div className="grid grid-cols-2 gap-x-6 gap-y-1">
          <DetailRow
            icon={UserCircle}
            label="联系人姓名"
            value={profile?.emergencyContactName}
            labelClassName="w-28"
          />
          <DetailRow
            icon={Phone}
            label="联系人手机号"
            value={profile?.emergencyContactPhone}
            labelClassName="w-28"
          />
          <DetailRow
            icon={Users}
            label="与员工关系"
            value={profile?.emergencyContactRelationship}
            labelClassName="w-28"
          />
        </div>
      </div>
    </div>
  );
};

/**
 * 解析员工所属组织 ID（供角色权限查询）
 * @param detail 员工详情
 */
const resolveEmployeeOrgId = (detail: EmployeeDetailVO): string =>
  detail.workIdentities?.find((item) => item.employeeId === detail.employeeId)?.orgId ??
  detail.workIdentities?.[0]?.orgId ??
  "";

/** 工作信息 Tab props */
export interface WorkInfoTabProps {
  detail: EmployeeDetailVO;
  onOpenRole: (params: RolePermOpenParams) => void;
}

/**
 * 员工详情 - 工作信息 Tab
 */
export const WorkInfoTab = ({ detail, onOpenRole }: WorkInfoTabProps) => {
  const work = detail.workInfo;
  const roleName = work?.staffRoleLabel ?? work?.staffRole ?? "";
  const identity = getPrimaryWorkIdentity(detail);
  const unitMeta = resolveOrgTypeMeta(identity?.templateType);
  const shopName = work?.shopName ?? detail.shopName ?? "";
  const unitDisplay = shopName
    ? unitMeta
      ? `${shopName}（${unitMeta.shortLabel}）`
      : shopName
    : "—";
  const contractType = work?.contractType;
  const contractStatusNode = work?.contractStatusLabel ? (
    <Tag bordered color="success" className="m-0 text-xs">
      {work.contractStatusLabel}
    </Tag>
  ) : (
    "—"
  );

  return (
    <div className="space-y-4">
      <div>
        <SectionTitle icon={Briefcase} title="基本工作信息" />
        <div className="grid grid-cols-2 gap-x-6 gap-y-1">
          <DetailRow icon={Hash} label="员工编号" value={work?.employeeNo} labelClassName="w-24" />
          <DetailRow icon={Building} label="所属单位" value={unitDisplay} labelClassName="w-24" />
          <div className="flex items-center gap-2 py-1 min-w-0">
            <UserCircle className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <span className="text-xs text-slate-500 shrink-0 w-24">默认角色</span>
            <div className="flex items-center gap-1.5 min-w-0 flex-1">
              <span className="text-xs text-slate-800 truncate">{roleName || "—"}</span>
              {roleName ? (
                <button
                  type="button"
                  className="text-xs text-orange-600 hover:underline inline-flex items-center gap-0.5 shrink-0"
                  onClick={() => {
                    const orgId = resolveEmployeeOrgId(detail);
                    if (!orgId) return;
                    onOpenRole({
                      roleName,
                      source: { mode: "employee", employeeId: detail.employeeId, orgId },
                    });
                  }}
                  disabled={!resolveEmployeeOrgId(detail)}
                >
                  <ShieldCheck className="w-3 h-3" />
                  查看权限
                </button>
              ) : null}
            </div>
          </div>
          <DetailRow
            icon={Calendar}
            label="入职日期"
            value={work?.entryDate}
            labelClassName="w-24"
          />
          <DetailRow
            icon={Clock}
            label="工作制度"
            value={work?.workTypeLabel ?? work?.workType}
            labelClassName="w-24"
          />
        </div>
      </div>
      <Divider className="my-2" />
      <div>
        <SectionTitle icon={FileText} title="合同信息" />
        <div className="grid grid-cols-2 gap-x-6 gap-y-1">
          <DetailRow
            icon={FileText}
            label="合同类型"
            type="dict"
            dictCode="employeeContractType"
            value={contractType}
            labelClassName="w-28"
          />
          <DetailRow
            icon={Calendar}
            label="合同开始时间"
            value={work?.contractStartDate}
            labelClassName="w-28"
          />
          <DetailRow
            icon={Calendar}
            label="合同结束时间"
            value={work?.contractEndDate}
            labelClassName="w-28"
          />
          <div className="flex items-center gap-2 py-1 min-w-0">
            <CircleCheckBig className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            <span className="text-xs text-slate-500 shrink-0 w-28">合同状态</span>
            <div className="flex-1 min-w-0">{contractStatusNode}</div>
          </div>
          <DetailRow
            icon={Paperclip}
            label="合同附件"
            value={work?.contractFileUrl ? "查看附件" : "—"}
            copyable={false}
            onClick={
              work?.contractFileUrl
                ? () => window.open(work.contractFileUrl, "_blank", "noopener,noreferrer")
                : undefined
            }
            labelClassName="w-28"
          />
        </div>
      </div>
      <Divider className="my-2" />
      <div>
        <SectionTitle icon={DollarSign} title="薪资信息" />
        <div className="grid grid-cols-2 gap-x-6 gap-y-1">
          <DetailRow
            icon={DollarSign}
            label="试用期薪资"
            value={formatMonthlySalary(work?.probationSalary)}
            labelClassName="w-24"
          />
          <DetailRow
            icon={DollarSign}
            label="转正薪资"
            value={formatMonthlySalary(work?.regularSalary)}
            labelClassName="w-24"
          />
        </div>
      </div>
      <Divider className="my-2" />
      <div>
        <SectionTitle icon={CreditCard} title="银行卡信息" />
        <div className="grid grid-cols-2 gap-x-6 gap-y-1">
          <DetailRow
            icon={UserCircle}
            label="开户人"
            value={work?.bankAccountHolder}
            labelClassName="w-24"
          />
          <DetailRow
            icon={Landmark}
            label="开户银行"
            value={work?.bankName ?? work?.designatedBankLabel}
            labelClassName="w-24"
          />
          <DetailRow
            icon={CreditCard}
            label="银行账号"
            value={work?.bankAccountMask}
            labelClassName="w-24"
          />
        </div>
      </div>
      <Divider className="my-2" />
      <div>
        <SectionTitle icon={Shield} title="社保信息" />
        <div className="grid grid-cols-2 gap-x-6 gap-y-1">
          <DetailRow
            icon={Shield}
            label="社保账号"
            value={work?.socialSecurityNo}
            labelClassName="w-24"
          />
          <DetailRow
            icon={Shield}
            label="公积金账号"
            value={work?.housingFundNo}
            labelClassName="w-24"
          />
        </div>
      </div>
    </div>
  );
};

/** 证照卡片 props */
interface CertificateCardProps {
  cert: EmployeeCertificateVO;
}

/** 证照卡片 */
const CertificateCard = ({ cert }: CertificateCardProps) => {
  const title = cert.certName ?? cert.certType ?? "证照";
  const showRemaining =
    cert.daysRemaining != null && cert.daysRemaining >= 0 && cert.daysRemaining <= 30;

  return (
    <div className="rounded-lg border border-slate-200 overflow-hidden bg-white">
      <div className="aspect-4/3 bg-slate-100 flex items-center justify-center">
        <OssPreviewImage
          fileUrl={cert.fileUrl}
          alt={title}
          className="w-full h-full object-cover"
          fallback={<Hexagon className="w-10 h-10 text-slate-300" />}
        />
      </div>
      <div className="p-3 space-y-1">
        <div className="text-sm font-medium text-slate-800">{title}</div>
        <div className="text-xs text-slate-400">{cert.expireDate ?? "—"}</div>
        <div className="flex flex-wrap gap-1 pt-0.5">
          {showRemaining ? (
            <Tag bordered color="blue" className="m-0 text-[10px]">
              还剩{cert.daysRemaining}天
            </Tag>
          ) : null}
          {cert.certStatus ? (
            <Tag bordered color="success" className="m-0 text-[10px]">
              {cert.certStatus}
            </Tag>
          ) : null}
        </div>
      </div>
    </div>
  );
};

/** 证照信息 Tab props */
export interface LicenseTabProps {
  detail: EmployeeDetailVO;
}

/**
 * 员工详情 - 证照信息 Tab
 */
export const LicenseTab = ({ detail }: LicenseTabProps) => {
  const certificates = detail.certificates ?? [];

  return (
    <div className="max-h-[50vh] overflow-y-auto">
      {certificates.length === 0 ? (
        <p className="text-sm text-slate-400 text-center py-8">暂无证照信息</p>
      ) : (
        <div className="grid grid-cols-2 gap-4">
          {certificates.map((cert) => (
            <CertificateCard
              key={cert.certificateId ?? cert.certName ?? cert.certType}
              cert={cert}
            />
          ))}
        </div>
      )}
    </div>
  );
};
